import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  changeMyPassword,
  getAllowedUserPreferences,
  getApiErrorMessage,
  getMyPreferences,
  updateMyPreferences,
} from '../../services/api';
import { getAuthProvider, setAuthToken } from '../../services/auth';

function emptyPreferencesMap(keys: string[]): Record<string, string> {
  const o: Record<string, string> = {};
  keys.forEach((k) => {
    o[k] = '';
  });
  return o;
}

function mergePreferenceValues(
  keys: string[],
  existing?: Record<string, string> | null
): Record<string, string> {
  const base = emptyPreferencesMap(keys);
  if (!existing) return base;
  keys.forEach((k) => {
    if (existing[k] != null && existing[k] !== '') base[k] = String(existing[k]);
  });
  return base;
}

function compactStringMap(m: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(m).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') out[k] = String(v).trim();
  });
  return out;
}

const PREFERENCE_LABELS: Record<string, string> = {
  display_date_format: 'Date Format',
  display_theme: 'Theme',
  home_page: 'Home Page',
};

function labelForPreferenceKey(key: string): string {
  return PREFERENCE_LABELS[key] ?? key;
}

interface PasswordFieldProps {
  label: string;
  value: string;
  autoComplete: string;
  show: boolean;
  onChange: (value: string) => void;
  onToggle: () => void;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  value,
  autoComplete,
  show,
  onChange,
  onToggle,
}) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    type={show ? 'text' : 'password'}
    value={value}
    autoComplete={autoComplete}
    onChange={(e) => onChange(e.target.value)}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label={show ? `Hide ${label}` : `Show ${label}`}
            onClick={onToggle}
            edge="end"
            size="small"
          >
            {show ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

export interface UserPreferencesDialogProps {
  open: boolean;
  onClose: () => void;
}

const UserPreferencesDialog: React.FC<UserPreferencesDialogProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allowedKeys, setAllowedKeys] = useState<string[]>([]);
  const [valueOptionsByKey, setValueOptionsByKey] = useState<Record<string, string[]>>({});
  const [form, setForm] = useState<Record<string, string>>({});
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    if (!open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setPasswordSaving(false);
      setPasswordError('');
      setPasswordSuccess('');
      setAuthProvider(null);
      return;
    }

    let cancelled = false;
    getAuthProvider()
      .then((provider) => {
        if (!cancelled) setAuthProvider(provider);
      })
      .catch(() => {
        if (!cancelled) setAuthProvider('default');
      });

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [allowedRes, mineRes] = await Promise.all([
          getAllowedUserPreferences(),
          getMyPreferences(),
        ]);
        if (cancelled) return;
        const payload = allowedRes.data;
        const keys = payload.allowed_user_preferences || [];
        setAllowedKeys(keys);
        setValueOptionsByKey(payload.value_options_by_key || {});
        setForm(mergePreferenceValues(keys, mineRes.data.preferences));
      } catch (e: unknown) {
        const err = e as { response?: { data?: { detail?: string } }; message?: string };
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load preferences');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSave = async () => {
    const payload = compactStringMap(form);
    if (Object.keys(payload).length === 0) {
      setError('Choose at least one preference value.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await updateMyPreferences(payload);
      setAuthToken(res.data.access_token);
      onClose();
      window.location.reload();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err.response?.data?.detail || err.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword.trim()) {
      setPasswordError('Enter your current password.');
      return;
    }
    if (!newPassword.trim()) {
      setPasswordError('Enter a new password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from the current password.');
      return;
    }
    setPasswordSaving(true);
    try {
      await changeMyPassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess('Password changed. Use it the next time you sign in.');
    } catch (e: unknown) {
      setPasswordError(getApiErrorMessage(e, 'Failed to change password'));
    } finally {
      setPasswordSaving(false);
    }
  };

  const showChangePassword = authProvider != null && authProvider !== 'azure';
  const busy = saving || passwordSaving;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>My settings</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These settings are stored on your account. After saving, the page reloads so your new session uses the
          updated preferences everywhere.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {allowedKeys.map((key) => (
              <FormControl key={key} fullWidth size="small">
                <InputLabel id={`pref-${key}`}>{labelForPreferenceKey(key)}</InputLabel>
                <Select
                  labelId={`pref-${key}`}
                  label={labelForPreferenceKey(key)}
                  value={form[key] ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      [key]: e.target.value as string,
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>Not set</em>
                  </MenuItem>
                  {(valueOptionsByKey[key] || []).map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
        )}

        {showChangePassword && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Change password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your current password, then choose a new one.
            </Typography>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
                {passwordError}
              </Alert>
            )}
            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess('')}>
                {passwordSuccess}
              </Alert>
            )}
            <Box
              component="form"
              onSubmit={handleChangePassword}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <PasswordField
                label="Current password"
                value={currentPassword}
                autoComplete="current-password"
                show={showCurrentPassword}
                onChange={setCurrentPassword}
                onToggle={() => setShowCurrentPassword((prev) => !prev)}
              />
              <PasswordField
                label="New password"
                value={newPassword}
                autoComplete="new-password"
                show={showNewPassword}
                onChange={setNewPassword}
                onToggle={() => setShowNewPassword((prev) => !prev)}
              />
              <PasswordField
                label="Confirm new password"
                value={confirmPassword}
                autoComplete="new-password"
                show={showConfirmPassword}
                onChange={setConfirmPassword}
                onToggle={() => setShowConfirmPassword((prev) => !prev)}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained" disabled={busy}>
                  {passwordSaving ? 'Changing…' : 'Change password'}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || busy}>
          {saving ? 'Saving…' : 'Save preferences'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserPreferencesDialog;
